import type React from 'react';
import { type PropsWithChildren, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { variant as navVariant } from '@cx-web/domain-types';
import classnames from 'classnames';
import { useOnClickOutside } from 'usehooks-ts';

import { useDesktopHeaderTracking } from '../../context/DesktopHeaderTrackingContext';
import caretStyles from '../dropdownCaret.module.css';

import styles from './CustomDropdown.module.css';

const CustomDropdown: React.FC<
  PropsWithChildren<{
    dropdownRef: React.RefObject<HTMLDivElement | null>;
    isUkStyle?: boolean;
    label: string;
    variant?: 'secondary' | 'tertiary';
  }>
> = ({ children, dropdownRef, isUkStyle, label = 'More', variant }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const track = useDesktopHeaderTracking();

  // Registered on mount and guarded by refs, so it is immune to the Astro
  // island-vs-document event-dispatch quirk that made a listener added during
  // the opening click close the dropdown within that same click. Mirrors
  // NavExpandable in HeaderNavItem.tsx.
  useOnClickOutside(ref as React.RefObject<HTMLElement>, event => {
    const target = event.target as Node | null;
    if (dropdownRef.current && target && dropdownRef.current.contains(target)) {
      return;
    }
    setIsOpen(false);
  });

  if (!children) {
    return null;
  }

  // The secondary redesign panel is gated behind isUkStyle (RENDER_UK_HEADER_STYLE), not just the
  // variant, so a secondary SPLIT_EXPANDABLE served with the flag off falls back to the plain
  // .dropdownContainer instead of leaking the UK restyle into the legacy header. When it applies,
  // the panel's own max-height (.secondaryDropdownContainer) is used instead of the inline default.
  const isSecondaryUk = variant === 'secondary' && Boolean(isUkStyle);

  const renderDropdownLinks = () => {
    if (isOpen) {
      return (
        dropdownRef.current &&
        createPortal(
          <div
            className={classnames(styles.dropdownContainer, isSecondaryUk && styles.secondaryDropdownContainer)}
            data-testid="custom-dropdown-modal"
            style={isSecondaryUk ? undefined : { maxHeight: `calc(100vh - 154px)` }}
          >
            {children}
          </div>,
          dropdownRef.current,
        )
      );
    }

    return (
      <div className={styles.hidden} data-testid="custom-dropdown-ssr-links">
        {children}
      </div>
    );
  };

  return (
    <>
      <div
        className={classnames(styles.dropdownLabel, isSecondaryUk && styles.dropdownLabelSecondaryUk)}
        data-testid="custom-dropdown-button"
        onClick={() => {
          if (!isOpen) {
            // PROMO_CODE and LINK_GROUP are tertiary-only and omit variant.
            track(variant ?? navVariant.tertiary, label);
          }
          setIsOpen(isOpen => !isOpen);
        }}
        ref={ref}
      >
        {label}
        <span
          aria-hidden="true"
          className={classnames(caretStyles.caret, isOpen && caretStyles.caretOpen)}
          data-testid="custom-dropdown-caret"
        />
      </div>
      {renderDropdownLinks()}
    </>
  );
};

export default CustomDropdown;
